import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@material-ui/core';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useEffect, useState } from 'react';
import type { AddMemberParams } from '../actions/addMember';
import addMember from '../actions/addMember';
import type { AddMemberParams as AddMemberParamsAdmin } from '../actions/admin/addMember';
import addMemberAdmin from '../actions/admin/addMember';
import updateMemberAdmin, {
    UpdateMemberParams,
} from '../actions/admin/updateMember';
import { Member } from '../actions/getMembers';
import { ReactComponent as MemberIcon } from '../icons/member.svg';
import Dialog from './Dialog';

dayjs.extend(utc);

interface EditMemberFormProps {
    open: boolean;
    onClose: () => void;
    isAddMode?: boolean;
    isAdmin?: boolean;
    member?: Member;
    userId?: number;
    onMemberUpdated?: () => void;
}

const EditMemberForm = ({
    open,
    onClose,
    isAddMode = false,
    isAdmin = false,
    member,
    userId,
    onMemberUpdated,
}: EditMemberFormProps) => {
    const [sex, setSex] = useState<'male' | 'female'>(
        isAddMode ? 'male' : member?.Sex || 'male'
    );
    const [birthDate, setBirthDate] = useState<dayjs.Dayjs | null>(
        isAddMode
            ? null
            : member?.BirthDate
              ? dayjs.utc(member.BirthDate)
              : null
    );
    const [formData, setFormData] = useState({
        firstName: isAddMode ? '' : member?.FirstName || '',
        lastName: isAddMode ? '' : member?.LastName || '',
        patientId: isAddMode ? '' : member?.PatientId || '',
        referringPhysician: isAddMode ? '' : member?.ReferringPhysician || '',
        ethnicity: isAddMode ? '' : member?.Ethnicity || '',
    });

    useEffect(() => {
        if (!isAddMode && member) {
            setFormData({
                firstName: member.FirstName || '',
                lastName: member.LastName || '',
                patientId: member.PatientId || '',
                referringPhysician: member.ReferringPhysician || '',
                ethnicity: member.Ethnicity || '',
            });
            setSex(member.Sex || 'male');
            setBirthDate(member.BirthDate ? dayjs.utc(member.BirthDate) : null);
        }
    }, [member, isAddMode]);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ): void => {
        const { name, value } = e.currentTarget;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = (e: React.SyntheticEvent) => {
        e.preventDefault();

        const target = e.target as typeof e.target & {
            firstName: { value: string };
            lastName: { value: string };
            patientId: { value: string | null };
            referringPhysician: { value: string | null };
            ethnicity: { value: string | null };
        };

        const firstName = target.firstName.value.trim();
        const lastName = target.lastName.value.trim();
        const patientId = target.patientId.value?.trim() || undefined;
        const referringPhysician =
            target.referringPhysician.value?.trim() || undefined;
        const ethnicity = target.ethnicity.value?.trim() || undefined;

        let birthDateConverted: Date | undefined = undefined;

        if (isAddMode) {
            if (!birthDate) {
                console.error(
                    'Birth date is required when adding a new member'
                );
                return;
            }
            birthDateConverted = birthDate.toDate();
        } else {
            birthDateConverted = birthDate ? birthDate.toDate() : undefined;
        }

        if (isAdmin) {
            if (isAddMode) {
                if (userId !== undefined) {
                    const data: AddMemberParamsAdmin = {
                        userId,
                        firstName,
                        lastName,
                        sex,
                        birthDate: birthDateConverted!,
                        patientId,
                        referringPhysician,
                        ethnicity,
                    };
                    addMemberAdmin(data);
                } else {
                    console.warn('userId is undefined!');
                }
            } else {
                if (member !== undefined) {
                    const data: UpdateMemberParams = {
                        firstName,
                        lastName,
                        sex,
                        birthDate: birthDateConverted,
                        patientId: patientId || null,
                        memberId: member.MemberId,
                        referringPhysician: referringPhysician || null,
                        ethnicity: ethnicity || null,
                    };
                    updateMemberAdmin(data);
                } else {
                    console.warn('member is undefined!');
                }
            }
        } else {
            if (isAddMode) {
                const data: AddMemberParams = {
                    firstName,
                    lastName,
                    sex,
                    birthDate: birthDateConverted!,
                    patientId,
                    referringPhysician,
                    ethnicity,
                };
                addMember(data);
            }
        }

        setFormData({
            firstName: '',
            lastName: '',
            patientId: '',
            referringPhysician: '',
            ethnicity: '',
        });
        setSex('male');
        setBirthDate(null);
        onMemberUpdated?.();
        onClose();
    };

    if (isAdmin && !userId) {
        return null;
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            onSubmit={handleSubmit}
            title={isAddMode ? 'Add New Member' : 'Edit Member'}
            icon={<MemberIcon />}
        >
            <TextField
                autoFocus
                required
                margin="dense"
                id="firstName"
                name="firstName"
                label="First Name"
                type="text"
                fullWidth
                variant="outlined"
                autoComplete="given-name"
                value={formData.firstName}
                onChange={handleInputChange}
                inputProps={{
                    maxLength: 100,
                }}
            />
            <TextField
                required
                margin="dense"
                id="lastName"
                name="lastName"
                label="Last Name"
                type="text"
                fullWidth
                variant="outlined"
                autoComplete="family-name"
                value={formData.lastName}
                onChange={handleInputChange}
                inputProps={{
                    maxLength: 200,
                }}
            />
            <FormControl fullWidth margin="dense">
                <InputLabel id="sex-select-label">Gender</InputLabel>
                <Select
                    labelId="sex-select-label"
                    id="sex-select"
                    name="sex"
                    label="Gender"
                    onChange={(e) =>
                        setSex(e.target.value as 'male' | 'female')
                    }
                    required
                    displayEmpty
                    value={sex}
                >
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    label="Birth Date"
                    value={birthDate}
                    onChange={(newValue) => {
                        if (!newValue) {
                            setBirthDate(null);
                            return;
                        }
                        const utcDate = dayjs.utc(
                            newValue.format('YYYY-MM-DD')
                        ); // Ensures midnight UTC
                        setBirthDate(utcDate);
                    }}
                    format="DD/MM/YYYY"
                    slotProps={{
                        textField: {
                            variant: 'outlined',
                            margin: 'dense',
                            fullWidth: true,
                            required: true,
                            name: 'birthDate',
                        },
                    }}
                    disableFuture
                />
            </LocalizationProvider>
            <TextField
                margin="dense"
                id="patientId"
                name="patientId"
                label="Patient ID (e.g. MRN No, Hospital or Clinic ID)"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.patientId}
                onChange={handleInputChange}
                inputProps={{
                    maxLength: 100,
                }}
            />
            <TextField
                margin="dense"
                id="ethnicity"
                name="ethnicity"
                label="Ethnicity/Origin"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.ethnicity}
                onChange={handleInputChange}
                required={isAddMode}
                inputProps={{
                    maxLength: 100,
                }}
            />
            <TextField
                margin="dense"
                id="referringPhysician"
                name="referringPhysician"
                label="Referring Physician"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.referringPhysician}
                onChange={handleInputChange}
                inputProps={{
                    maxLength: 200,
                }}
            />
        </Dialog>
    );
};

export default EditMemberForm;
